import {observable, action, computed, makeObservable, reaction} from 'mobx'
import {injectable} from 'inversify'

import {ROUTES} from '@/router/routes'
import {
    OnboardingStep,
    ApplicationStep,
    ApplicationSubStep
} from '@/stores/onboarding/models/onboarding'
import {IApplicationStepsStore} from '@/stores/onboarding/IApplicationStepsStore'
import {OnboardingStore} from './OnboardingStore'
import {getOnboardingStore} from './utils'

@injectable()
export class ApplicationStepsStore implements IApplicationStepsStore {
    steps: ApplicationStep[] = []
    currentStepKey = ''

    private onboardingStore: OnboardingStore

    constructor() {
        this.onboardingStore = getOnboardingStore()

        makeObservable(this, {
            steps: observable,
            currentStepKey: observable,
            generateSteps: action.bound,
            isStepCompleted: action.bound,
            getStepValidationTooltip: action.bound,
            isStepClickable: action.bound,
            isNextAvailableStep: action.bound,
            updateCurrentStep: action.bound,
            allStepsFlat: computed
        })

        // Set up reactions to update steps when data changes
        reaction(
            () => [
                this.onboardingStore.currentStep,
                this.onboardingStore.stepCompletionStatus,
                this.onboardingStore.processStatus,
                this.onboardingStore.companyDetails,
                this.onboardingStore.companyContacts,
                this.onboardingStore.owners.length,
                this.onboardingStore.directors.length,
                this.onboardingStore.corporateOwners.length,
                this.onboardingStore.bankDetails,
                this.onboardingStore.stores.length,
                this.onboardingStore.companyDetails?.entityType,
                // Track changes to completion status of individual items
                this.onboardingStore.owners.map((o) => o.isComplete).join(','),
                this.onboardingStore.directors.map((d) => d.isComplete).join(','),
                this.onboardingStore.corporateOwners.map((c) => c.isComplete).join(','),
                this.onboardingStore.bankDetails?.accounts?.map((a) => a.isComplete).join(','),
                this.onboardingStore.validationResponse
            ],
            () => {
                this.steps = this.generateSteps()
            },
            {fireImmediately: true}
        )
    }

    get allStepsFlat(): (ApplicationStep | ApplicationSubStep)[] {
        const allSteps: (ApplicationStep | ApplicationSubStep)[] = []
        this.steps.forEach((step) => {
            if (step.subSteps) {
                allSteps.push(...step.subSteps)
            } else {
                allSteps.push(step)
            }
        })
        return allSteps
    }

    isStepCompleted(step: OnboardingStep): boolean {
        if (this.onboardingStore.validationResponse) {
            return this.onboardingStore.stepCompletionStatus[step] || false
        }

        switch (step) {
            case OnboardingStep.COMPANY_DETAILS:
                return this.onboardingStore.checkCompanyDetailsComplete()
            case OnboardingStep.COMPANY_CONTACTS:
                return this.onboardingStore.checkCompanyContactsComplete()
            case OnboardingStep.OWNERS_DIRECTORS:
                return this.onboardingStore.checkOwnersDirectorsComplete()
            case OnboardingStep.BANK_DETAILS:
                return this.onboardingStore.checkBankDetailsComplete()
            case OnboardingStep.OUTLETS_INFORMATION:
                return this.onboardingStore.checkOutletsComplete()
            default:
                return this.onboardingStore.stepCompletionStatus[step] || false
        }
    }

    private isStepDisabled(step: OnboardingStep): boolean {
        return false
    }

    private isStepActive(step: OnboardingStep): boolean {
        // If we have a URL-based currentStepKey, use that for active state
        // Otherwise fall back to onboardingStore.currentStep
        if (this.currentStepKey) {
            const stepKeyMap = {
                [OnboardingStep.COMPANY_DETAILS]: 'company-details',
                [OnboardingStep.COMPANY_CONTACTS]: 'company-contacts',
                [OnboardingStep.OWNERS_DIRECTORS]: 'owners-directors',
                [OnboardingStep.BANK_DETAILS]: 'bank-details',
                [OnboardingStep.OUTLETS_INFORMATION]: 'outlets-information',
                [OnboardingStep.REVIEW_SUBMIT]: 'review-submit',
                [OnboardingStep.APPLICATION_PROGRESS]: 'application-progress'
            }
            return stepKeyMap[step] === this.currentStepKey
        }
        return this.onboardingStore.currentStep === step
    }

    generateSteps(): ApplicationStep[] {
        const steps: ApplicationStep[] = []

        steps.push(
            {
                key: 'company',
                title: 'Company details',
                completed:
                    this.isStepCompleted(OnboardingStep.COMPANY_DETAILS) &&
                    this.isStepCompleted(OnboardingStep.COMPANY_CONTACTS),
                active:
                    this.isStepActive(OnboardingStep.COMPANY_DETAILS) ||
                    this.isStepActive(OnboardingStep.COMPANY_CONTACTS),
                expanded: true,
                subSteps: [
                    {
                        key: 'company-details',
                        title: 'Company details',
                        route: ROUTES.onboarding.companyDetails,
                        completed: this.isStepCompleted(OnboardingStep.COMPANY_DETAILS),
                        active: this.isStepActive(OnboardingStep.COMPANY_DETAILS),
                        disabled: this.isStepDisabled(OnboardingStep.COMPANY_DETAILS)
                    },
                    {
                        key: 'company-contacts',
                        title: 'Company contacts',
                        route: ROUTES.onboarding.companyContacts,
                        completed: this.isStepCompleted(OnboardingStep.COMPANY_CONTACTS),
                        active: this.isStepActive(OnboardingStep.COMPANY_CONTACTS),
                        disabled: this.isStepDisabled(OnboardingStep.COMPANY_CONTACTS)
                    }
                ]
            },
            {
                key: 'owners-directors',
                title: 'Owners & Directors',
                route: ROUTES.onboarding.ownersDirectors,
                completed: this.isStepCompleted(OnboardingStep.OWNERS_DIRECTORS),
                active: this.isStepActive(OnboardingStep.OWNERS_DIRECTORS),
                disabled: this.isStepDisabled(OnboardingStep.OWNERS_DIRECTORS)
            },
            {
                key: 'bank-details',
                title: 'Bank details',
                route: ROUTES.onboarding.bankDetails,
                completed: this.isStepCompleted(OnboardingStep.BANK_DETAILS),
                active: this.isStepActive(OnboardingStep.BANK_DETAILS),
                disabled: this.isStepDisabled(OnboardingStep.BANK_DETAILS)
            },
            {
                key: 'outlets-information',
                title: 'Outlets and Products',
                route: ROUTES.onboarding.outletsInformation,
                completed: this.isStepCompleted(OnboardingStep.OUTLETS_INFORMATION),
                active: this.isStepActive(OnboardingStep.OUTLETS_INFORMATION),
                disabled: this.isStepDisabled(OnboardingStep.OUTLETS_INFORMATION)
            },
            {
                key: 'review-submit',
                title: 'Review & Submit',
                route: ROUTES.onboarding.reviewSubmit,
                completed: this.onboardingStore.stepCompletionStatus[OnboardingStep.REVIEW_SUBMIT],
                active: this.isStepActive(OnboardingStep.REVIEW_SUBMIT),
                disabled: this.isStepDisabled(OnboardingStep.REVIEW_SUBMIT)
            }
        )

        if (
            this.onboardingStore.stepCompletionStatus[OnboardingStep.REVIEW_SUBMIT] ||
            this.isStepActive(OnboardingStep.APPLICATION_PROGRESS) ||
            this.onboardingStore.processStatus === 'in_progress'
        ) {
            steps.push({
                key: 'application-progress',
                title: 'Application Progress',
                route: ROUTES.onboarding.applicationProgress,
                completed:
                    this.onboardingStore.stepCompletionStatus[OnboardingStep.APPLICATION_PROGRESS],
                active: this.isStepActive(OnboardingStep.APPLICATION_PROGRESS),
                disabled: this.isStepDisabled(OnboardingStep.APPLICATION_PROGRESS)
            })
        }

        return steps
    }

    getStepValidationTooltip(stepKey: string): string | null {
        let onboardingStep: OnboardingStep | null = null

        switch (stepKey) {
            case 'company-details':
                onboardingStep = OnboardingStep.COMPANY_DETAILS
                break
            case 'company-contacts':
                onboardingStep = OnboardingStep.COMPANY_CONTACTS
                break
            case 'owners-directors':
                onboardingStep = OnboardingStep.OWNERS_DIRECTORS
                break
            case 'bank-details':
                onboardingStep = OnboardingStep.BANK_DETAILS
                break
            case 'outlets-information':
                onboardingStep = OnboardingStep.OUTLETS_INFORMATION
                break
        }

        if (!onboardingStep) return null

        const validation = this.onboardingStore.getStepValidationDetails(onboardingStep)
        if (validation.isComplete || validation.missingFields.length === 0) return null

        // Format missing fields for display
        const missingCount = validation.missingFields.length
        if (missingCount > 3) {
            return `${missingCount} fields required`
        }

        return `Missing: ${validation.missingFields.slice(0, 3).join(', ')}`
    }

    isStepClickable(step: ApplicationStep | ApplicationSubStep): boolean {
        if (step.disabled || !step.route) return false

        if (step.completed) return true

        if (step.active) return true

        const allSteps = this.allStepsFlat
        const currentStepIndex = allSteps.findIndex((s) => s.key === step.key)

        if (currentStepIndex === 0) return true

        if (currentStepIndex > 0) {
            const previousStep = allSteps[currentStepIndex - 1]
            return previousStep.completed || false
        }

        return false
    }

    isNextAvailableStep(step: ApplicationStep | ApplicationSubStep): boolean {
        if (step.disabled || step.completed || step.active || !step.route) return false

        const allSteps = this.allStepsFlat
        const currentStepIndex = allSteps.findIndex((s) => s.key === step.key)

        if (currentStepIndex > 0) {
            const previousStep = allSteps[currentStepIndex - 1]
            return previousStep.completed || false
        }

        return currentStepIndex === 0
    }

    updateCurrentStep(pathname: string): void {
        let foundStep = false

        for (const step of this.steps) {
            if (step.subSteps) {
                const activeSubStep = step.subSteps.find((subStep) => subStep.route === pathname)
                if (activeSubStep) {
                    this.currentStepKey = activeSubStep.key
                    foundStep = true
                    break
                }
            }
        }

        if (!foundStep) {
            const activeStep = this.steps.find((step) => {
                if (step.route === pathname) return true
                return step.route && pathname.startsWith(step.route + '/')
            })
            if (activeStep) {
                this.currentStepKey = activeStep.key
            }
        }

        this.steps = this.generateSteps()
    }
}
